import { SuggestedSeriesDismissDialog } from 'src/component/lets-get-moving/suggested-series-dismiss-dialog'
import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageContainer } from 'src/component/page-container/page-container'
import { BackArrow } from 'src/component/programmes/programme-back-arrow'
import { OutlinedButton } from 'src/component/ui/button'
import { Grid } from 'src/component/ui/grid/grid'
import { PageTitle } from 'src/component/ui/page-title'
import { SeriesCardActive, SeriesCardSkeleton } from 'src/component/ui/series-card'
import { useCancelProgrammeDialog } from 'src/hook/use-cancel-programme-dialog'
import { useMyStartedProgrammesFromLibrary } from 'src/hook/use-programmes'
import { cn } from 'src/lib/utils'
import { toasterUtil } from 'src/util/toaster-util'

const PAGE_SIZE = 12

const cancelSeriesDescription = (
  <div className="text-mist font-roboto-condensed text-base tracking-wide space-y-2 leading-relaxed text-center">
    <p>Once cancelled, you will lose all completed workouts within this series.</p>
    <p>You can restart it at any time from the series library.</p>
  </div>
)

export const KeepGoingScreen = (): React.ReactElement => {
  const { requestCancel, dialogProps } = useCancelProgrammeDialog()
  const [page, setPage] = useState(1)
  const isFirstPageRender = useRef(true)

  const { data, isLoading, isError, isFetching } = useMyStartedProgrammesFromLibrary(
    { page, pageSize: PAGE_SIZE },
    {
      onError: () => {
        toasterUtil.error('We could not load your series in progress. Please try again.')
      },
    },
  )

  useEffect(() => {
    if (isFirstPageRender.current) {
      isFirstPageRender.current = false
      return
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [page])

  const programmes = data?.data.map((hp) => hp.programme) ?? []
  const totalSize = data?.totalSize ?? 0
  const totalPages = data?.totalPages ?? 0
  const currentPage = data?.page ?? page
  const resolvedPageSize = data?.pageSize ?? PAGE_SIZE

  const showInitialSkeleton = (isLoading || isError) && !data
  const isEmpty = !showInitialSkeleton && !isError && programmes.length === 0
  const showPagination = totalPages > 1

  const rangeStart = totalSize === 0 ? 0 : (currentPage - 1) * resolvedPageSize + 1
  const rangeEnd = Math.min(currentPage * resolvedPageSize, totalSize)

  return (
    <PageContainer>
      <div className="py-6 md:py-[82px]">
        <div className="mb-10">
          <BackArrow />
        </div>
        <div className="mb-10">
          <PageTitle>Keep going</PageTitle>
        </div>

        {showInitialSkeleton ? (
          <Grid.Skeleton count={PAGE_SIZE} columns={3}>
            <SeriesCardSkeleton variant="active" />
          </Grid.Skeleton>
        ) : isEmpty ? (
          <div className="text-center py-12">
            <p className="text-mist font-roboto-condensed text-lg mb-6">No series in progress from your library.</p>
            <OutlinedButton asChild className="border-primary text-primary">
              <Link to="/programmes">Browse series</Link>
            </OutlinedButton>
          </div>
        ) : (
          <>
            <div className={cn(isFetching && 'opacity-60 transition-opacity')}>
              <Grid
                items={programmes}
                keyExtractor={(p) => p.id}
                renderItem={(programme) => (
                  <SeriesCardActive series={programme} onCancelSeriesClick={() => requestCancel(programme)} />
                )}
              />
            </div>

            <SuggestedSeriesDismissDialog
              {...dialogProps}
              title="Cancel series?"
              description={cancelSeriesDescription}
              confirmLabel="Cancel series"
            />

            {showPagination ? (
              <div className="flex flex-wrap justify-between items-center gap-4 mt-10 pt-8 border-t border-white/10">
                <OutlinedButton
                  type="button"
                  variant="outline"
                  disabled={currentPage <= 1 || isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </OutlinedButton>
                <span className="text-mist font-roboto-condensed text-sm text-center order-3 sm:order-none w-full sm:w-auto">
                  Showing {rangeStart}–{rangeEnd} of {totalSize}
                  <span className="text-white/40"> · </span>
                  Page {currentPage} of {totalPages}
                </span>
                <OutlinedButton
                  type="button"
                  variant="outline"
                  disabled={currentPage >= totalPages || isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </OutlinedButton>
              </div>
            ) : null}
          </>
        )}
      </div>
    </PageContainer>
  )
}
